import { gql } from "graphql-tag";
import {
  ProductShortFragment,
  ProductSummaryFragment,
} from "entity/product/api/fragments";
import { CategoryFragment } from "entity/category/api/fragments";

export const SettingsFragment = gql`
  fragment SettingsFragment on Settings {
    minSumDelivery
    minSumPickup
    minSumPickupFirst
    minPriceCourierDelivery
    minSumFreeDelivery
  }
`;

export const QuestionFragment = gql`
  fragment QuestionFragment on Question {
    question
    answer
  }
`;

export const RegionFragment = gql`
  fragment RegionFragment on Region {
    id
    lat
    long
    name
    num
    names {
      i
      r
      p
    }
    cityNames {
      i
      r
      p
    }
  }
`;

export const GeoObjectFragment = gql`
  fragment GeoObjectFragment on GeoObject {
    id
    code
    name
    type
    lat
    long
    num
  }
`;

export const UserProfileFragment = gql`
  fragment UserProfileFragment on UserProfile {
    birthday
    email
    gender
    id
    name
    patronymic
    phone
    surname
    points
    isPromoCodeAvailable
    isEmployee
    isEmployeeModeEnable
    isOperator
    favoriteProducts {
      items
      total
    }
    card {
      number
      status
      type
    }
  }
`;

export const SessionFragment = gql`
  fragment SessionFragment on Session {
    token {
      accessToken
      refreshToken
    }
    profile {
      ...UserProfileFragment
    }
  }
  ${UserProfileFragment}
`;

export const HomeBlogCategoryFragment = gql`
  fragment HomeBlogCategoryFragment on BlogCategory {
    alias
    id
    name
  }
`;

/* todo: remove it */
export const MainCategoryFragment = gql`
  fragment MainCategoryFragment on MainCategory {
    id
    name
    alias
    url
    path
    level
    isMeta
    productOfDay {
      ...ProductSummaryFragment
    }
    img
    background
  }
  ${ProductSummaryFragment}
`;

export const GroupFragment = gql`
  fragment GroupFragment on Group {
    code
    mnns {
      href
      title
    }
    name
    instruction
    icd10 {
      code
      desc
    }
    certificates {
      link
      title
    }
    photos {
      link
      title
    }
    forms {
      name
      items {
        dosage
        quantity
      }
    }
    analogs(paginator: $paginator) {
      items {
        ...ProductSummaryFragment
      }
      total
    }
  }
  ${ProductSummaryFragment}
`;

export const CartItemFragment = gql`
  fragment CartItemFragment on CartItem {
    amount
    price
    quantity
    product {
      ...ProductShortFragment
    }
    totalAmount
    totalDiscount
    priceTypeID
    isChecked
    isExpirating
  }
  ${ProductShortFragment}
`;

export const FiltersConfigFragment = gql`
  fragment FiltersConfigFragment on FiltersConfig {
    options {
      id
      placeholder
      title
      type
      available
    }
    lists {
      id
      title
      items {
        id
        title
        subtitle
        isSelected
        count
      }
      searchPlaceholder
      allowsMultipleSelection
      isSearchEnabled
    }
    ranges {
      id
      title
      min
      max
    }
    tags {
      id
      name
      title
      url
    }
    categories {
      ...CategoryFragment
      children @skip(if: $withoutSubcategories) {
        ...CategoryFragment
      }
    }
    defaultSort {
      side
      type
    }
    toggles {
      id
      title
    }
  }
  ${CategoryFragment}
`;

export const MetaInformationFragment = gql`
  fragment MetaInformationFragment on MetaInformation {
    title
    description
    h1
    breadcrumbs {
      title
      link
    }
    metadata {
      name
      content
    }
    microdata
  }
`;

export const PharmacyFragment = gql`
  fragment PharmacyFragment on Pharmacy {
    id
    name
    address
    lat
    long
    regionID
    city
    district
    street
    home
    building
    office
    phoneContract
    addressComment
    phones
    categories
    rating
    isFavorite
    isZdravcity
    isFromLastOrder
    metro {
      name
    }
    term {
      pickupDays
      bookingDays
    }
    mapsComments {
      name
      icon
      url
    }
    workingHours {
      name
      open
      close
    }
    paymentTypes
  }
`;

export const ClusterFragment = gql`
  fragment ClusterFragment on Cluster {
    id
    count
    lat
    long
    selected
  }
`;

export const PromoFragment = gql`
  fragment PromoFragment on Promo {
    activeTo
    alias
    description
    id
    name
    type
    ... on PromoSimple {
      pictureSrc
    }
    ... on PromoOffline {
      pharmacies
      pictureSrc
    }
  }
`;

export const ProductInstructionsFragment = gql`
  fragment ProductInstructionsFragment on ProductInstruction {
    shortDesc
    indictation
    contraIndication
    specialInstruction
    pharmAction
    dosage
    interaction
    overDosage
    sideEffect
    formulation
    storingCondition
    atcCode
    startRegDate
    endRegDate
    reRegDate
    numberReg
    gtin
    registeredAs
    urlToRegistry
    internationalName
    icd10 {
      code
      desc
    }
  }
`;

export const LoyaltyFragment = gql`
  fragment LoyaltyFragment on Loyalty {
    type
    selected
    params {
      points {
        available
        selected
        count
        error
      }
      promo {
        available
        selected
        code
        error
        errorDescription
      }
    }
  }
`;

export const PaymentFragment = gql`
  fragment PaymentFragment on Payment {
    type
    selected
    email
  }
`;

export const CartTotalFragment = gql`
  fragment CartTotalFragment on CartTotal {
    amount
    totalAmount
    productsAmount
    productsCount
    discount
    points
    hasBookingOnlyPrices
    needTotalPrefix
    quantity
    courierPrice
    minSum
    button {
      available
      message
    }
    hasPrescripted
  }
`;

export const PreOrderOptionsFragment = gql`
  fragment PreOrderOptionsFragment on PreOrderOptions {
    delivery {
      date
      interval
      payment {
        ...PaymentFragment
      }
      address {
        id
        isSelected
        formattedAddress
        entrance
        office
        floor
        comment
      }
      addressError
    }
    pickup {
      payment {
        ...PaymentFragment
      }
    }
  }
  ${PaymentFragment}
`;

export const PreOrderAvailableOptionsFragment = gql`
  fragment PreOrderAvailableOptionsFragment on PreOderAvailableOptions {
    delivery {
      dates {
        id
        text
        label
        selected
        intervals {
          id
          text
          price
          isFree
          isExpress
          selected
          tariffId
        }
      }
      addresses {
        id
        isSelected
        formattedAddress
        entrance
        office
        floor
        comment
      }
      paymentOptions {
        ...PaymentFragment
      }
    }
    pickup {
      payments {
        ...PaymentFragment
      }
    }
  }
  ${PaymentFragment}
`;

export const DeliveryParamsFragment = gql`
  fragment DeliveryParamsFragment on DeliveryParams {
    address
    comment
    dateText
  }
`;

export const PreOrderFragment = gql`
  fragment PreOrderFragment on PreOrder {
    step
    groups {
      items {
        ...CartItemFragment
        availableForSelect
        isExpirating
      }
    }
    total {
      ...CartTotalFragment
    }
    loyalty {
      ...LoyaltyFragment
    }
    options {
      ...PreOrderOptionsFragment
    }
    availableOptions {
      ...PreOrderAvailableOptionsFragment
    }
    deliveryParams {
      ...DeliveryParamsFragment
    }
    seller {
      name
      address
      number
    }
    pharmacy {
      ...PharmacyFragment
    }
    receiveTypes {
      type
      isChecked
      amount
    }
  }
  ${DeliveryParamsFragment}
  ${CartItemFragment}
  ${CartTotalFragment}
  ${LoyaltyFragment}
  ${PreOrderOptionsFragment}
  ${PreOrderAvailableOptionsFragment}
  ${PharmacyFragment}
`;

export const AdmitadCountersFragment = gql`
  fragment AdmitadCountersFragment on AdmitadCounters {
    transactionChannel
    transactionAction
    tariffCode
  }
`;

export const OrderFragment = gql`
  fragment OrderFragment on Order {
    id
    type
    receiveType
    canBeCancel
    paymentParams {
      url
      token
      mode
      type
      returnURL
    }
    paymentStatus {
      status
      label
    }
    deliveryParams {
      ...DeliveryParamsFragment
    }
    pharmacy {
      ...PharmacyFragment
      phones
    }
    items {
      ...CartItemFragment
    }
    total {
      ...CartTotalFragment
    }
    counters {
      admitad {
        ...AdmitadCountersFragment
      }
    }
  }
  ${DeliveryParamsFragment}
  ${PharmacyFragment}
  ${CartItemFragment}
  ${CartTotalFragment}
  ${AdmitadCountersFragment}
`;

export const BrandFragment = gql`
  fragment BrandFragment on Brand {
    id
    alias
    name
    image
    description
    rating
    country
    certificates
  }
`;

export const DeliveryItemFragment = gql`
  fragment DeliveryItemFragment on DeliveryItem {
    id
    url
    name
    image
    amount
    totalAmount
    available
  }
`;

export const DeliveryFragment = gql`
  fragment DeliveryFragment on Delivery {
    type
    amount
    totalAmount
    points
    isChecked
    receiveType
    items {
      ...DeliveryItemFragment
    }
    buttonText
    minPriceError
    isOfflinePromo
  }
  ${DeliveryItemFragment}
`;

export const PickupStoreFragment = gql`
  fragment PickupStoreFragment on PickupStore {
    pharmacy {
      ...PharmacyFragment
    }
    deliveries {
      ...DeliveryFragment
    }
  }
  ${PharmacyFragment}
  ${DeliveryFragment}
`;

export const PickupStorePointFragment = gql`
  fragment PickupStorePointFragment on PickupStorePoint {
    pharmacy {
      __typename
      ...PharmacyFragment
    }
    available
    selected
    availableProduct
  }
  ${PharmacyFragment}
`;
